/**
 * Booking Calendar Page Component
 * /admin/bookings/calendar
 */

import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  IconButton,
} from '@mui/material';
import TodayIcon from '@mui/icons-material/Today';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import apiClient from '../api/client';

const BookingCalendar = () => {
  const [view, setView] = useState(0); // 0: day, 1: week, 2: month
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadCalendar();
  }, [view, date]);

  const loadCalendar = async () => {
    try {
      setLoading(true);
      setError('');
      let response;
      if (view === 0) {
        response = await apiClient.getBookingCalendarDay(date);
      } else if (view === 1) {
        response = await apiClient.getBookingCalendarWeek(date);
      } else {
        response = await apiClient.getBookingCalendarMonth(date);
      }
      const data = Array.isArray(response) ? response : (response.bookings || response.data || []);
      setBookings(data);
    } catch (err) {
      setError(err.message || 'Failed to load calendar');
    } finally {
      setLoading(false);
    }
  };

  const handleViewChange = (event, newValue) => {
    setView(newValue);
  };

  const handleDateChange = (days) => {
    const currentDate = new Date(date);
    currentDate.setDate(currentDate.getDate() + days);
    setDate(currentDate.toISOString().split('T')[0]);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      confirmed: 'info',
      completed: 'success',
      cancelled: 'error',
      in_progress: 'primary',
    };
    return colors[status] || 'default';
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const viewLabels = ['Day', 'Week', 'Month'];

  return (
    <Container maxWidth="lg">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Booking Calendar
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => handleDateChange(-1)}>
            <ArrowBackIosIcon />
          </IconButton>
          <Typography variant="body1">{date}</Typography>
          <IconButton onClick={() => handleDateChange(1)}>
            <ArrowForwardIosIcon />
          </IconButton>
          <Button onClick={() => setDate(new Date().toISOString().split('T')[0])}>
            Today
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={view} onChange={handleViewChange} aria-label="calendar view tabs">
          <Tab icon={<TodayIcon />} label="Day" />
          <Tab icon={<ViewWeekIcon />} label="Week" />
          <Tab icon={<CalendarMonthIcon />} label="Month" />
        </Tabs>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {viewLabels[view]} View - {bookings.length} Bookings
          </Typography>
          <Grid container spacing={2}>
            {bookings.length === 0 ? (
              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                  No bookings found for this {viewLabels[view].toLowerCase()}
                </Typography>
              </Grid>
            ) : (
              bookings.map((booking) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={booking.id}>
                  <Card>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {booking.booking_number || booking.id.substring(0, 8)}
                        </Typography>
                        <Chip
                          label={booking.status || 'pending'}
                          color={getStatusColor(booking.status)}
                          size="small"
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Customer: {booking.customer_name || booking.customer_email || '-'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Service: {booking.service_type || '-'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Vehicle: {booking.vehicle_plate_number || booking.vehicle || '-'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Start: {formatDateTime(booking.start_datetime)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        End: {formatDateTime(booking.end_datetime)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))
            )}
          </Grid>
        </Paper>
      )}
    </Container>
  );
};

export default BookingCalendar;

